import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { adminApi, parseCommands, formatCommands } from "@/api";
import { Modal, ConfirmModal } from "@/components/Modal";

const HINT_FMT = 'Format : /i /ms "Nom du token" /qv 0xVALEUR /lang en-US. Appliqué à toutes les machines du vendor sauf override.';
const HINT_SAFE = "Script allégé appliqué quand le nom de la carte mère contient un des chipsets listés. Priorité : override carte exacte → Intel Safe → générique Intel.";
const DEFAULT_SAFE_CHIPSETS = "H110, B150, H170, B250, H270, H310, B360, H370, H410, B460, H470, H510, B560, H570";

export default function ScriptsTab({ onChanged }) {
  const [profiles, setProfiles] = useState([]);
  const [vendor, setVendor] = useState("AMD");
  const [text, setText] = useState("");
  const [chipsets, setChipsets] = useState(DEFAULT_SAFE_CHIPSETS);
  const [showOvr, setShowOvr] = useState(false);
  const [ovr, setOvr] = useState({ cpu_vendor: "AMD", board_model: "", bios_version: "", commands: "", note: "" });
  const [del, setDel] = useState(null);
  const isSafe = vendor === "IntelSafe";

  const load = useCallback(async () => {
    try {
      const r = await adminApi().get("/admin/profiles");
      setProfiles(r.data);
    } catch {
      toast.error("Erreur de chargement des profils");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (vendor === "IntelSafe") {
      const safe = profiles.find((p) => p.category === "SAFE");
      setText(safe ? formatCommands(safe.commands) : "");
      setChipsets(safe && safe.chipsets && safe.chipsets.length ? safe.chipsets.join(", ") : DEFAULT_SAFE_CHIPSETS);
      return;
    }
    const generic = profiles.find((p) => p.cpu_vendor === vendor.toUpperCase() && !p.board_model && !p.bios_version && !p.category);
    setText(generic ? formatCommands(generic.commands) : "");
  }, [vendor, profiles]);

  const saveGeneric = async () => {
    const commands = parseCommands(text);
    if (!commands.length) { toast.error("Le script est vide"); return; }
    if (isSafe) {
      const list = chipsets.split(",").map((c) => c.trim()).filter(Boolean);
      if (!list.length) { toast.error("La liste de chipsets est vide"); return; }
      try {
        await adminApi().put("/admin/profiles", { cpu_vendor: "Intel", category: "safe", chipsets: list, commands, board_model: null, bios_version: null, note: null });
        toast.success("Script Intel Safe enregistré");
        load();
        onChanged?.();
      } catch {
        toast.error("Erreur d'enregistrement");
      }
      return;
    }
    try {
      await adminApi().put("/admin/profiles", { cpu_vendor: vendor, board_model: null, bios_version: null, commands, note: null });
      toast.success(`Script ${vendor} enregistré`);
      load();
      onChanged?.();
    } catch {
      toast.error("Erreur d'enregistrement");
    }
  };

  const saveOverride = async () => {
    const commands = parseCommands(ovr.commands);
    if (!ovr.board_model.trim()) { toast.error("Renseigne la carte mère"); return; }
    if (!commands.length) { toast.error("Le script est vide"); return; }
    try {
      await adminApi().put("/admin/profiles", {
        cpu_vendor: ovr.cpu_vendor,
        board_model: ovr.board_model.trim(),
        bios_version: ovr.bios_version.trim() || null,
        commands,
        note: ovr.note || null,
      });
      toast.success("Override enregistré");
      setShowOvr(false);
      setOvr({ cpu_vendor: "AMD", board_model: "", bios_version: "", commands: "", note: "" });
      load();
      onChanged?.();
    } catch {
      toast.error("Erreur d'enregistrement");
    }
  };

  const deleteProfile = async () => {
    try {
      await adminApi().delete(`/admin/profiles/${encodeURIComponent(del.profile_id)}`);
      toast.success("Profil supprimé");
      setDel(null);
      load();
      onChanged?.();
    } catch {
      toast.error("Erreur");
      setDel(null);
    }
  };

  const overrides = profiles.filter((p) => p.board_model);

  return (
    <div className="grid2" data-testid="scripts-tab">
      <div className="card">
        <h2>Script générique par processeur</h2>
        <div className="vtabs">
          <button className={vendor === "AMD" ? "on" : ""} onClick={() => setVendor("AMD")} data-testid="vendor-tab-amd">AMD</button>
          <button className={vendor === "Intel" ? "on" : ""} onClick={() => setVendor("Intel")} data-testid="vendor-tab-intel">Intel</button>
          <button className={isSafe ? "on" : ""} onClick={() => setVendor("IntelSafe")} data-testid="vendor-tab-intel-safe">Intel Safe</button>
        </div>
        {isSafe && (
          <div className="fld" style={{ marginBottom: 10 }}>
            <label>Chipsets concernés — séparés par des virgules</label>
            <input
              className="mono"
              value={chipsets}
              onChange={(e) => setChipsets(e.target.value)}
              placeholder="H510, B560, H410, B460…"
              data-testid="safe-chipsets-input"
            />
          </div>
        )}
        <label className="lbl" style={{ display: "block", marginBottom: 7 }}>Commandes SCEWIN — une par ligne</label>
        <textarea
          className="mono"
          rows={9}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={'/i /ms "Above 4G Decoding" /qv 0x1 /lang en-US'}
          data-testid="generic-script-textarea"
        />
        <div className="hint">{isSafe ? HINT_SAFE : HINT_FMT}</div>
        <button className="btn btn-p" onClick={saveGeneric} data-testid="save-generic-script-btn">Enregistrer</button>
        <div className="warn">
          Un token erroné peut rendre une machine instable ou impossible à démarrer.
          N'enregistre que des valeurs testées sur le matériel réel — les offsets changent entre versions de BIOS.
        </div>
      </div>

      <div className="card">
        <h2>Overrides par carte / BIOS</h2>
        <div className="hint" style={{ marginTop: 0 }}>
          Prioritaires sur le générique. Résolution : carte + BIOS → carte → générique vendor.
        </div>
        {overrides.length === 0 && <div className="hint">Aucun override.</div>}
        {overrides.map((p) => (
          <div className="ovr" key={p.profile_id} data-testid={`override-${p.profile_id}`}>
            <span style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span className={`tag ${p.cpu_vendor === "AMD" ? "t-amd" : "t-intel"}`}>{p.cpu_vendor}</span>
              <span className="mono">{p.board_model}</span>
              {p.bios_version && <span className="tag t-mut mono">BIOS {p.bios_version}</span>}
            </span>
            <span style={{ display: "flex", gap: 6 }}>
              <button
                className="btn btn-sm"
                onClick={() => {
                  setOvr({
                    cpu_vendor: p.cpu_vendor === "AMD" ? "AMD" : "Intel",
                    board_model: p.board_model,
                    bios_version: p.bios_version || "",
                    commands: formatCommands(p.commands),
                    note: p.note || "",
                  });
                  setShowOvr(true);
                }}
                data-testid={`edit-override-btn-${p.profile_id}`}
              >
                Modifier
              </button>
              <button className="btn btn-sm btn-danger" onClick={() => setDel(p)} data-testid={`delete-override-btn-${p.profile_id}`}>
                Supprimer
              </button>
            </span>
          </div>
        ))}
        <button className="btn" style={{ marginTop: 8 }} onClick={() => setShowOvr(true)} data-testid="add-override-btn">
          Ajouter un override
        </button>
      </div>

      {showOvr && (
        <Modal title="Override carte / BIOS" onClose={() => setShowOvr(false)}>
          <div className="fld">
            <label>Vendor CPU</label>
            <select value={ovr.cpu_vendor} onChange={(e) => setOvr({ ...ovr, cpu_vendor: e.target.value })} data-testid="override-vendor-select">
              <option>AMD</option>
              <option>Intel</option>
            </select>
          </div>
          <div className="fld">
            <label>Carte mère (board_model)</label>
            <input className="mono" value={ovr.board_model} onChange={(e) => setOvr({ ...ovr, board_model: e.target.value })} placeholder="ex : TUF GAMING B550-PLUS" data-testid="override-board-input" />
          </div>
          <div className="fld">
            <label>Version BIOS — vide = toutes versions</label>
            <input className="mono" value={ovr.bios_version} onChange={(e) => setOvr({ ...ovr, bios_version: e.target.value })} placeholder="ex : 3607" data-testid="override-bios-input" />
          </div>
          <div className="fld">
            <label>Commandes SCEWIN — une par ligne</label>
            <textarea className="mono" rows={5} value={ovr.commands} onChange={(e) => setOvr({ ...ovr, commands: e.target.value })} data-testid="override-commands-textarea" />
          </div>
          <div className="fld">
            <label>Note (optionnel)</label>
            <input value={ovr.note} onChange={(e) => setOvr({ ...ovr, note: e.target.value })} data-testid="override-note-input" />
          </div>
          <div className="actions">
            <button className="btn" onClick={() => setShowOvr(false)}>Annuler</button>
            <button className="btn btn-p" onClick={saveOverride} data-testid="save-override-btn">Enregistrer</button>
          </div>
        </Modal>
      )}

      {del && (
        <ConfirmModal
          title="Supprimer le profil"
          body={`Le profil ${del.profile_id} sera supprimé. Les machines concernées retomberont sur le profil plus générique.`}
          danger
          onConfirm={deleteProfile}
          onClose={() => setDel(null)}
        />
      )}
    </div>
  );
}
